/**
 * Cruscotto commerciale licenze (SuperAdmin MAE).
 *
 * Aggrega lato FE da:
 *   - GET /api/tenant-subscriptions?include=licensePlan,tenant
 *   - GET /api/license-pricings?filter[isActive]=true&include=licensePlan
 *   - GET /api/license-histories?include=tenant&sort=-createdAt
 *
 * NB naming BE: la relationship al piano si chiama `licensePlan` (property
 * `LicensePlan`), NON `plan`. Gli attributi dello storico sono `eventType`/`reason`
 * (non `action`/`details`). Con AllowUnknownQueryStringParameters=true un include
 * sbagliato non dà 400 ma viene ignorato → dati vuoti silenziosi: qui è corretto.
 */
import { createFileRoute } from '@tanstack/react-router';
import { mw, MW_CARD_SHADOW } from '@/shared/mobile/tokens';
import { Center, Group, NumberFormatter, ScrollArea, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { AreaChart } from '@mantine/charts';
import {
  IconAlertCircle,
  IconArrowDownRight,
  IconArrowRight,
  IconArrowUpRight,
  IconCash,
  IconCoin,
  IconCrown,
  IconHistory,
  IconTrendingUp,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { fetchAllPages, fetchList } from '@/shared/api/jsonapi/client';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import type { LicensePricing, TenantSubscription } from '@/shared/types/api.gen';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { Paginated } from '@/shared/components/DataTable/TablePagination';

export const Route = createFileRoute('/_app/admin/license-dashboard')({ component: AdminDashboardPage });

type SubRow = TenantSubscription & Denormalized & {
  id: string;
  licensePlan?: { id: string; code?: string; name?: string };
  tenant?: { id: string; name?: string };
};

type PricingRow = LicensePricing & Denormalized & {
  id: string;
  licensePlan?: { id: string; code?: string };
};

interface LicenseHistoryRow extends Denormalized {
  id: string;
  eventType?: string;
  reason?: string | null;
  actorType?: string | null;
  licenseCode?: string | null;
  createdAt?: string;
  tenant?: { id: string; name?: string };
}

const ACTIVE_STATUSES = new Set(['ACTIVE', 'TRIAL', 'GRACE_PERIOD']);

const CARD: CSSProperties = {
  border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW,
};
const TH: CSSProperties = {
  background: mw.surface2, color: mw.ink300, fontSize: 'var(--fs-compact)', fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${mw.line}`, whiteSpace: 'nowrap',
};

const chip = (label: string, bg: string, color: string) => (
  <span style={{ display: 'inline-block', fontSize: 'var(--fs-compact)', fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: bg, color, whiteSpace: 'nowrap' }}>{label}</span>
);

function AdminDashboardPage() {
  const subsQ = useQuery({
    queryKey: ['admin-all-subscriptions'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchAllPages<SubRow>('tenant-subscriptions', {
      include: ['licensePlan', 'tenant'],
      sort: ['-createdAt']
    }, 500),
  });

  const pricingsQ = useQuery({
    queryKey: ['admin-all-pricings'],
    staleTime: 30 * 60_000,
    queryFn: () => fetchAllPages<PricingRow>('license-pricings', {
      filter: { isActive: { eq: true } },
      include: ['licensePlan']
    }, 500),
  });

  const historyQ = useQuery({
    queryKey: ['admin-license-histories'],
    staleTime: 60_000,
    queryFn: () => fetchList<LicenseHistoryRow>('license-histories', {
      include: ['tenant'],
      sort: ['-createdAt'],
      page: { size: 100 },
    }),
  });

  const snap = useMemo(() => {
    const subs = subsQ.data ?? [];
    const pricings = pricingsQ.data ?? [];

    // Mappa pricing per (planId, currency, billingPeriod) → pricing più recente.
    const pricingByKey = new Map<string, PricingRow>();
    for (const p of pricings) {
      const planId = p.licensePlan?.id;
      if (!planId) continue;
      const key = `${planId}|${p.currency ?? 'EUR'}|${p.billingPeriod ?? 'monthly'}`;
      const prev = pricingByKey.get(key);
      if (!prev || (p.validFrom && (!prev.validFrom || p.validFrom > prev.validFrom))) {
        pricingByKey.set(key, p);
      }
    }

    const monthlyRevenue = (sub: SubRow): number => {
      const planId = sub.licensePlan?.id;
      if (!planId) return 0;
      const currency = sub.currency ?? 'EUR';
      const period = sub.billingPeriod ?? 'monthly';
      const pricing = pricingByKey.get(`${planId}|${currency}|${period}`);
      if (!pricing) return 0;
      const seats = sub.seats ?? 1;
      const base = pricing.basePrice ?? 0;
      const perSeat = pricing.pricePerSeat ?? 0;
      const gross = base + perSeat * seats;
      if (period === 'yearly') return gross / 12;
      if (period === 'quarterly') return gross / 3;
      return gross;
    };

    const activeSubs = subs.filter((s) => s.status && ACTIVE_STATUSES.has(s.status));
    const trialSubs = subs.filter((s) => s.status === 'TRIAL');
    const paidSubs = subs.filter((s) => s.status === 'ACTIVE');

    const mrr = activeSubs.reduce((acc, s) => acc + monthlyRevenue(s), 0);

    const everInTrial = subs.filter((s) => s.trialEndsAt != null).length;
    const convertedFromTrial = paidSubs.filter((s) => s.trialEndsAt != null).length;
    const trialToPaidPct = everInTrial > 0 ? (convertedFromTrial / everInTrial) * 100 : 0;

    const thirtyDaysAgo = dayjs().subtract(30, 'day');
    const churnedRecent = subs.filter((s) => s.cancelledAt && dayjs(s.cancelledAt).isAfter(thirtyDaysAgo));
    const churnDenom = activeSubs.length + churnedRecent.length;
    const churnPct = churnDenom > 0 ? (churnedRecent.length / churnDenom) * 100 : 0;

    const trend: { month: string; mrr: number; newCustomers: number; churnedCustomers: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const m = dayjs().subtract(i, 'month');
      const monthStart = m.startOf('month');
      const monthEnd = m.endOf('month');
      const activeInMonth = subs.filter((s) => {
        if (!s.startDate) return false;
        if (dayjs(s.startDate).isAfter(monthEnd)) return false;
        if (s.cancelledAt && dayjs(s.cancelledAt).isBefore(monthStart)) return false;
        if (s.endDate && dayjs(s.endDate).isBefore(monthStart)) return false;
        return true;
      });
      const monthMrr = activeInMonth.reduce((acc, s) => acc + monthlyRevenue(s), 0);
      const inMonth = (iso: string) => {
        const d = dayjs(iso);
        return d.isSameOrAfter(monthStart) && d.isSameOrBefore(monthEnd);
      };
      const newC = subs.filter((s) => s.startDate && inMonth(s.startDate)).length;
      const churnedC = subs.filter((s) => s.cancelledAt && inMonth(s.cancelledAt)).length;
      trend.push({ month: m.format('YYYY-MM'), mrr: Math.round(monthMrr), newCustomers: newC, churnedCustomers: churnedC });
    }

    const lastMrr = trend.at(-1)?.mrr ?? 0;
    const prevMrr = trend.at(-2)?.mrr ?? 0;
    const mrrDeltaPct = prevMrr > 0 ? ((lastMrr - prevMrr) / prevMrr) * 100 : undefined;

    return {
      mrr: Math.round(mrr),
      mrrDeltaPct,
      activeTenants: activeSubs.length,
      activeTenantsDelta: (trend.at(-1)?.newCustomers ?? 0) - (trend.at(-1)?.churnedCustomers ?? 0),
      trialCount: trialSubs.length,
      trialToPaidPct,
      churnPct,
      churnedRecent,
      trend,
      currency: subs[0]?.currency ?? 'EUR',
    };
  }, [subsQ.data, pricingsQ.data]);

  const isLoading = subsQ.isLoading || pricingsQ.isLoading;
  const cur = snap.currency === 'EUR' ? '€ ' : '$ ';

  const isMobile = useIsMobileShell();

  if (isLoading) {
    const skel = (
      <>
        <InlineLoader label="Caricamento della dashboard licenze…" minHeight={120} />
        <InlineLoader label="Caricamento della dashboard licenze…" minHeight={160} />
      </>
    );
    return isMobile ? (
      <Stack gap={16} px={16} pt={16} pb={24}>
        <MobileHeader title="Cruscotto commerciale" />
        {skel}
      </Stack>
    ) : (
      <MaeShell>
        <InlineLoader label="Caricamento della dashboard licenze…" minHeight={160} />
        {skel}
      </MaeShell>
    );
  }

  const noData = (subsQ.data ?? []).length === 0;

  const body = (
    <>
      {noData && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: `1px solid ${mw.warningLine}`, background: mw.warningBg, borderRadius: 14, padding: '12px 16px', marginBottom: 16 }}>
          <IconAlertCircle size={18} style={{ color: mw.warning, flexShrink: 0, marginTop: 1 }} />
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.5 }}>
            <strong style={{ color: mw.ink900 }}>Nessuna sottoscrizione registrata.</strong> Il cruscotto mostrerà KPI reali appena verranno create le prime sub via <code>/api/tenants/&#123;id&#125;/subscribe</code>.
          </Text>
        </div>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={14} mb={18}>
        <MaeKpi label="MRR" tone="bordeaux" icon={IconCash} deltaPct={snap.mrrDeltaPct}
          value={<NumberFormatter value={snap.mrr} prefix={cur} thousandSeparator="." decimalSeparator="," decimalScale={0} />} />
        <MaeKpi label="Aziende attive" tone="azzurro" icon={IconUsersGroup} delta={snap.activeTenantsDelta} value={snap.activeTenants} />
        <MaeKpi label="Trial → Paid" tone="success" icon={IconTrendingUp} value={`${snap.trialToPaidPct.toFixed(0)}%`} />
        <MaeKpi label="Churn 30g" tone="warning" icon={IconCoin} value={`${snap.churnPct.toFixed(1)}%`} />
      </SimpleGrid>

      <div style={{ ...CARD, padding: 20, marginBottom: 16 }}>
        <Group justify="space-between" mb={14}>
          <div>
            <div style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: mw.ink300 }}>Andamento</div>
            <Text style={{ fontSize: 'var(--fs-lead)', fontWeight: 700, color: mw.ink900, marginTop: 2 }}>Trend MRR · ultimi 12 mesi</Text>
          </div>
          {snap.trend.length > 0 && chip(`${cur}${(snap.trend.at(-1)?.mrr ?? 0).toLocaleString('it-IT')}`, mw.brandTint, mw.brand)}
        </Group>
        {snap.trend.some((t) => t.mrr > 0) ? (
          <AreaChart
            h={280}
            data={snap.trend.map((t) => ({ month: dayjs(`${t.month}-01`).format('MMM YY'), MRR: t.mrr }))}
            dataKey="month"
            series={[{ name: 'MRR', color: mw.brand }]}
            curveType="monotone"
            tickLine="y"
            gridAxis="y"
            withGradient
            withTooltip
            valueFormatter={(v) => `${cur}${v.toLocaleString('it-IT')}`}
          />
        ) : (
          <Center h={280}>
            <div style={{ textAlign: 'center' }}>
              <span style={{ display: 'inline-flex', width: 52, height: 52, borderRadius: 14, background: mw.surface2, color: mw.ink300, alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                <IconTrendingUp size={26} />
              </span>
              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>Trend non calcolabile: nessun pricing collegato alle subscription.</Text>
            </div>
          </Center>
        )}
      </div>

      <div style={{ ...CARD, overflow: 'hidden', marginBottom: 16 }}>
        <Group justify="space-between" p={16} pb={10}>
          <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>Churn recenti · 30 giorni</Text>
          {chip(String(snap.churnedRecent.length), snap.churnedRecent.length > 0 ? mw.dangerBg : mw.surface2, snap.churnedRecent.length > 0 ? mw.danger : mw.ink500)}
        </Group>
        <Paginated rows={snap.churnedRecent} label="clienti">
        {(pageRows) => (
        <ScrollArea type="auto">
          <Table verticalSpacing={12} horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={TH}>Azienda</Table.Th>
                <Table.Th style={TH}>Piano</Table.Th>
                <Table.Th style={{ ...TH, textAlign: 'right' }}>Mesi attivo</Table.Th>
                <Table.Th style={TH}>Data churn</Table.Th>
                <Table.Th style={TH}>Motivo</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map((c) => {
                const months = c.startDate && c.cancelledAt ? Math.max(0, dayjs(c.cancelledAt).diff(dayjs(c.startDate), 'month')) : 0;
                return (
                  <Table.Tr key={c.id}>
                    <Table.Td><Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }}>{c.tenant?.name ?? c.tenant?.id ?? '—'}</Text></Table.Td>
                    <Table.Td>{c.licensePlan?.name || c.licensePlan?.code ? chip(c.licensePlan.name ?? c.licensePlan.code ?? '—', mw.accentTint, mw.accentText) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink900, fontVariantNumeric: 'tabular-nums' }}>{months}</Text></Table.Td>
                    <Table.Td><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{c.cancelledAt ? dayjs(c.cancelledAt).format('DD/MM/YYYY') : '—'}</Text></Table.Td>
                    <Table.Td><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }} lineClamp={2}>{c.cancellationReason ?? '—'}</Text></Table.Td>
                  </Table.Tr>
                );
              })}
              {snap.churnedRecent.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, textAlign: 'center', padding: '18px 0' }}>Nessun churn negli ultimi 30 giorni.</Text></Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        )}
        </Paginated>
      </div>

      <div style={{ ...CARD, overflow: 'hidden' }}>
        <Group justify="space-between" p={16} pb={10}>
          <Group gap={8}>
            <IconHistory size={17} style={{ color: mw.accentText }} />
            <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>Eventi licensing recenti</Text>
          </Group>
          {chip(String(historyQ.data?.data?.length ?? 0), mw.surface2, mw.ink500)}
        </Group>
        <ScrollArea type="auto">
          <Table verticalSpacing={12} horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={TH}>Data</Table.Th>
                <Table.Th style={TH}>Azienda</Table.Th>
                <Table.Th style={TH}>Evento</Table.Th>
                <Table.Th style={TH}>Origine</Table.Th>
                <Table.Th style={TH}>Motivo</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {historyQ.isError && <QueryErrorAlert error={historyQ.error} onRetry={() => { void historyQ.refetch(); }} />}
      {historyQ.isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <InlineLoader label="Caricamento dello storico…" minHeight={90} />
                  </Table.Td>
                </Table.Tr>
              )}
              {!historyQ.isLoading && (historyQ.data?.data ?? []).slice(0, 30).map((h) => (
                <Table.Tr key={h.id}>
                  <Table.Td><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink900 }}>{h.createdAt ? dayjs(h.createdAt).format('DD/MM/YYYY HH:mm') : '—'}</Text></Table.Td>
                  <Table.Td><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{h.tenant?.name ?? h.tenant?.id ?? '—'}</Text></Table.Td>
                  <Table.Td>{h.eventType ? chip(h.eventType, mw.accentTint, mw.accentText) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                  <Table.Td>{h.actorType ? chip(h.actorType, mw.surface2, mw.ink500) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                  <Table.Td><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }} lineClamp={2}>{h.reason ?? '—'}</Text></Table.Td>
                </Table.Tr>
              ))}
              {!historyQ.isLoading && (historyQ.data?.data ?? []).length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, textAlign: 'center', padding: '18px 0' }}>Nessun evento.</Text></Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Stack gap={16} px={16} pt={16} pb={24}>
        <MobileHeader title="Cruscotto commerciale" />
        {body}
      </Stack>
    );
  }

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconCrown size={22} />}
        title="Cruscotto commerciale"
        sub="MRR, aziende attive, trial-to-paid e churn cluster-wide (solo SuperAdmin)."
        tone="bordeaux"
      />
      {body}
    </MaeShell>
  );
}

type KpiTone = 'bordeaux' | 'azzurro' | 'success' | 'warning';
const KPI_TONES: Record<KpiTone, { fg: string; iconBg: string; iconFg: string }> = {
  bordeaux: { fg: mw.brand, iconBg: mw.brandTint, iconFg: mw.brand },
  azzurro: { fg: mw.accentText, iconBg: mw.accentTint, iconFg: mw.accentText },
  success: { fg: mw.success, iconBg: mw.successBg, iconFg: mw.success },
  warning: { fg: mw.warning, iconBg: mw.warningBg, iconFg: mw.warning },
};

function MaeKpi({ label, value, icon: Icon, tone, delta, deltaPct, deltaInverted }: {
  label: string;
  value: ReactNode;
  icon: typeof IconCash;
  tone: KpiTone;
  delta?: number;
  deltaPct?: number;
  deltaInverted?: boolean;
}) {
  const c = KPI_TONES[tone];
  const raw = deltaPct ?? delta;
  const hasDelta = raw != null && !Number.isNaN(raw);
  const sign = hasDelta ? Math.sign(raw) : 0;
  const good = deltaInverted ? sign < 0 : sign > 0;
  const dc = sign === 0
    ? { bg: mw.surface2, fg: mw.ink500 }
    : good
      ? { bg: mw.successBg, fg: mw.success }
      : { bg: mw.dangerBg, fg: mw.danger };
  const DeltaIcon = sign === 0 ? IconArrowRight : good ? IconArrowUpRight : IconArrowDownRight;

  return (
    <div style={{ ...CARD, padding: '16px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: mw.ink300 }}>{label}</div>
        <span style={{ width: 42, height: 42, borderRadius: 12, background: c.iconBg, color: c.iconFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={20} />
        </span>
      </div>
      <div style={{ fontSize: 'var(--fs-h2)', fontWeight: 700, color: c.fg, marginTop: 8, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{value}</div>
      {hasDelta && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 8, fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: dc.bg, color: dc.fg }}>
          <DeltaIcon size={12} />
          {deltaPct != null ? `${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%` : `${delta != null && delta > 0 ? '+' : ''}${delta} netti`}
        </span>
      )}
    </div>
  );
}
